import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtINR } from "./_shared";

// Date range options — must match the backend keys in /admin/analytics/executive-summary.
const RANGES = [
  { v: "today", l: "Today" },
  { v: "7d",    l: "7 Days" },
  { v: "30d",   l: "30 Days" },
  { v: "90d",   l: "90 Days" },
  { v: "all",   l: "All Time" },
];

// Fmt helpers — large numbers in lakh/crore, percentages with 1 decimal.
function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-IN").format(n);
}
function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function KpiCard({ label, value, accent, testid, helper }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#0E0E0E] border border-[#2A2A2A] p-4 hover:border-[#FF1E1E]/40 transition-colors"
    >
      <div className="text-[10px] font-mono-data tracking-widest text-white/40 uppercase mb-2">
        {label}
      </div>
      <div className={`font-display text-2xl leading-none font-extrabold ${accent ? "text-[#FF1E1E]" : "text-white"}`}>
        {value}
      </div>
      {helper && (
        <div className="text-[10px] font-mono-data text-white/35 mt-2 tracking-wide">
          {helper}
        </div>
      )}
    </div>
  );
}

function ScoreBar({ score }) {
  // 0–100 horizontal bar; color steps mirror the AMG accent system.
  const v = Math.max(0, Math.min(100, score || 0));
  const color = v >= 70 ? "#22C55E" : v >= 50 ? "#F5A623" : "#FF1E1E";
  return (
    <div className="w-full h-1.5 bg-[#1A1A1A] relative">
      <div
        className="absolute inset-y-0 left-0"
        style={{ width: `${v}%`, background: color }}
      />
    </div>
  );
}

function VehicleMobileCard({ row, rank, kind }) {
  // Mobile card variant — no horizontal table, dense info stack.
  const scoreClass = kind === "buy"
    ? row.buy_score >= 70 ? "text-emerald-400" : "text-amber-400"
    : "text-[#FF1E1E]";
  return (
    <div
      data-testid={`${kind}-row-${row.id}`}
      className="border-b border-[#1A1A1A] px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-[10px] font-mono-data text-white/40 mb-0.5">#{rank}</div>
          <div className="text-sm text-white font-display normal-case leading-tight truncate">
            {row.vehicle || "—"}
          </div>
          <div className="text-[10px] font-mono-data text-white/40 uppercase tracking-wider truncate">
            {row.registration_number || row.acquisition_stage}
            {row.age_days !== null && row.age_days !== undefined && (
              <span className="text-white/30 ml-2">{row.age_days}d</span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`text-base font-display font-extrabold leading-none ${scoreClass}`} data-testid={`${kind}-score-${row.id}`}>
            {row.buy_score.toFixed(1)}
          </div>
          <div className="mt-1 w-16"><ScoreBar score={row.buy_score} /></div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-[10px] font-mono-data">
        <div>
          <div className="text-white/40 uppercase tracking-wider">Offer</div>
          <div className="text-white">{fmtINR(row.offer)}</div>
        </div>
        <div>
          <div className="text-white/40 uppercase tracking-wider">Retail</div>
          <div className="text-white">{fmtINR(row.retail)}</div>
        </div>
        <div>
          <div className="text-white/40 uppercase tracking-wider">Liq</div>
          <div className="text-white">{row.liquidity === null || row.liquidity === undefined ? "—" : `${row.liquidity}/10`}</div>
        </div>
        <div>
          <div className="text-white/40 uppercase tracking-wider">ROI</div>
          <div className={row.roi >= 0 ? "text-emerald-400" : "text-[#FF1E1E]"}>{fmtPct(row.roi)}</div>
        </div>
      </div>
    </div>
  );
}

function VehicleRow({ row, rank, kind }) {
  // kind: "buy" → green-tinted score; "avoid" → red-tinted score.
  const scoreClass = kind === "buy"
    ? row.buy_score >= 70 ? "text-emerald-400" : "text-amber-400"
    : "text-[#FF1E1E]";

  return (
    <tr
      data-testid={`${kind}-row-${row.id}`}
      className="border-b border-[#1A1A1A] hover:bg-white/[0.02]"
    >
      <td className="px-3 py-2.5 text-[10px] font-mono-data text-white/45 w-10">#{rank}</td>
      <td className="px-3 py-2.5">
        <div className="text-xs text-white font-display normal-case leading-tight truncate max-w-[260px]">
          {row.vehicle || "—"}
        </div>
        <div className="text-[10px] font-mono-data text-white/40 uppercase tracking-wider truncate">
          {row.registration_number || row.acquisition_stage}
          {row.age_days !== null && row.age_days !== undefined && (
            <span className="text-white/30 ml-2">{row.age_days}d old</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right text-xs font-mono-data text-white">{fmtINR(row.offer)}</td>
      <td className="px-3 py-2.5 text-right text-xs font-mono-data text-white">{fmtINR(row.retail)}</td>
      <td className="px-3 py-2.5 text-right text-xs font-mono-data text-white/80">{fmtPct(row.gap_pct)}</td>
      <td className="px-3 py-2.5 text-right text-xs font-mono-data text-white/80">
        {row.liquidity === null || row.liquidity === undefined ? "—" : `${row.liquidity}/10`}
      </td>
      <td className={`px-3 py-2.5 text-right text-xs font-mono-data ${row.roi >= 0 ? "text-emerald-400" : "text-[#FF1E1E]"}`}>
        {fmtPct(row.roi)}
      </td>
      <td className="px-3 py-2.5 w-32">
        <div className={`text-sm font-display font-extrabold leading-none ${scoreClass}`} data-testid={`${kind}-score-${row.id}`}>
          {row.buy_score.toFixed(1)}
        </div>
        <div className="mt-1.5"><ScoreBar score={row.buy_score} /></div>
      </td>
    </tr>
  );
}

function CapitalTable({ title, subtitle, rows, kind, emptyHint }) {
  // Top 5 visible by default; "View More" expands to full ≤20.
  // Applies only when rows.length > 5 — otherwise show everything outright.
  const [expanded, setExpanded] = React.useState(false);
  const VISIBLE_DEFAULT = 5;
  const shown = expanded || rows.length <= VISIBLE_DEFAULT ? rows : rows.slice(0, VISIBLE_DEFAULT);
  const moreCount = Math.max(0, rows.length - VISIBLE_DEFAULT);

  return (
    <div className="qd-card p-0 overflow-hidden" data-testid={`capital-${kind}-table`}>
      <div className="px-4 sm:px-5 py-4 border-b border-[#1A1A1A] flex items-baseline justify-between">
        <div className="min-w-0">
          <div className="qd-overline truncate">{title}</div>
          <div className="text-[11px] text-white/45 mt-1 truncate">{subtitle}</div>
        </div>
        <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/40 shrink-0">
          {rows.length} unit{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          {emptyHint}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="text-[10px] font-mono-data tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Vehicle</th>
                  <th className="px-3 py-2 text-right">Offer</th>
                  <th className="px-3 py-2 text-right">Retail</th>
                  <th className="px-3 py-2 text-right">Gap %</th>
                  <th className="px-3 py-2 text-right">Liquidity</th>
                  <th className="px-3 py-2 text-right">ROI</th>
                  <th className="px-3 py-2 text-left">Buy Score</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r, i) => (
                  <VehicleRow key={r.id} row={r} rank={i + 1} kind={kind} />
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden">
            {shown.map((r, i) => (
              <VehicleMobileCard key={r.id} row={r} rank={i + 1} kind={kind} />
            ))}
          </div>

          {moreCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              data-testid={`capital-${kind}-toggle`}
              className="w-full px-5 py-3 text-[10px] font-mono-data tracking-widest uppercase text-[#FF1E1E] border-t border-[#1A1A1A] hover:bg-[#FF1E1E]/5 transition-colors"
            >
              {expanded ? "Show Top 5" : `View ${moreCount} more`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function ExecutiveDashboard() {
  const [range, setRange] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [capital, setCapital] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/admin/analytics/executive-summary?range=${range}`),
      api.get(`/admin/analytics/capital-deployment?limit=20`),
    ])
      .then(([s, c]) => {
        if (cancelled) return;
        setSummary(s.data);
        setCapital(c.data);
      })
      .catch(() => toast.error("Failed to load executive dashboard"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [range]);

  return (
    <section data-testid="executive-dashboard" className="mb-8">
      {/* Header + Range selector — fixed on mobile (sticky via parent has multiple
          gotchas with the admin layout); plain inline on desktop. The h-12 spacer
          below preserves document flow on mobile so content doesn't jump. */}
      <div
        className="fixed left-0 right-0 top-0 z-30 px-3 py-2 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#1A1A1A] md:static md:px-0 md:py-0 md:bg-transparent md:backdrop-blur-0 md:border-b-0 md:mb-4"
        data-testid="exec-range-bar"
      >
        <div className="flex items-center justify-between gap-2 md:items-end md:flex-wrap">
          <div className="min-w-0">
            <div className="qd-overline truncate">Executive Summary</div>
            <div className="text-[11px] text-white/45 mt-1 hidden md:block">
              Real-time pipeline health, capital exposure, and deal-ready opportunities.
            </div>
          </div>
          <div className="inline-flex border border-[#2A2A2A] bg-[#0E0E0E] overflow-x-auto max-w-[60%] md:max-w-full shrink-0" role="tablist" data-testid="exec-range-tabs">
            {RANGES.map((r) => (
              <button
                key={r.v}
                data-testid={`exec-range-${r.v}`}
                onClick={() => setRange(r.v)}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] font-mono-data tracking-widest uppercase border-r border-[#2A2A2A] last:border-r-0 transition-colors whitespace-nowrap ${
                  range === r.v ? "bg-[#FF1E1E]/15 text-[#FF1E1E]" : "text-white/55 hover:text-white"
                }`}
              >
                {r.l}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Spacer that reserves vertical space for the fixed bar on mobile only. */}
      <div className="h-12 md:hidden" aria-hidden="true" />

      {/* KPI Grid — 8 cards, 2/4 columns. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2A2A2A] mb-8" data-testid="exec-kpi-grid">
        <KpiCard
          label="Total Leads"
          value={loading ? "…" : fmtNum(summary?.total_leads)}
          testid="kpi-total-leads"
        />
        <KpiCard
          label="Active Leads"
          value={loading ? "…" : fmtNum(summary?.active_leads)}
          accent
          testid="kpi-active-leads"
          helper="non-closed pipeline"
        />
        <KpiCard
          label="Avg Gap %"
          value={loading ? "…" : fmtPct(summary?.avg_gap_pct)}
          testid="kpi-avg-gap"
          helper={summary ? `n=${summary.sample_size_with_gap}` : ""}
        />
        <KpiCard
          label="Avg Liquidity"
          value={loading ? "…" : (summary?.avg_liquidity ? `${summary.avg_liquidity}/10` : "—")}
          testid="kpi-avg-liquidity"
          helper={summary ? `n=${summary.sample_size_with_liquidity}` : ""}
        />
        <KpiCard
          label="Green Return Units"
          value={loading ? "…" : fmtNum(summary?.green_return_units)}
          accent
          testid="kpi-green-return"
          helper="net profit > 0"
        />
        <KpiCard
          label="Opportunity Units"
          value={loading ? "…" : fmtNum(summary?.opportunity_units)}
          testid="kpi-opportunity-units"
          helper="gap < 5%"
        />
        <KpiCard
          label="Expected Gross Profit"
          value={loading ? "…" : fmtINR(summary?.expected_gross_profit_pool)}
          testid="kpi-gross-pool"
          helper="retail − offer"
        />
        <KpiCard
          label="Expected Net Profit"
          value={loading ? "…" : fmtINR(summary?.expected_net_profit_pool)}
          accent
          testid="kpi-net-pool"
          helper="gross − recon"
        />
      </div>

      {/* Capital Deployment — Buy Now + Avoid */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="qd-overline">Capital Deployment</div>
          <div className="text-[11px] text-white/45 mt-1">
            Buy Score = 40% Liquidity · 30% ROI · 20% Gap · 10% Lead Age.
            {capital?.scored_total !== undefined && (
              <span className="text-white/30 ml-2">· {capital.scored_total} scoreable active leads</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <CapitalTable
          title="Top 20 · Buy Now"
          subtitle="Highest Buy Score · capital allocates here first"
          rows={capital?.buy_now || []}
          kind="buy"
          emptyHint={loading ? "Loading…" : "No scoreable active leads. Add liquidity scores + retail valuations to populate this list."}
        />
        <CapitalTable
          title="Top 20 · Avoid"
          subtitle="Lowest Buy Score · de-prioritize or pass"
          rows={capital?.avoid || []}
          kind="avoid"
          emptyHint={loading ? "Loading…" : "Not enough active leads to surface a distinct avoid list."}
        />
      </div>
    </section>
  );
}
